import { CancelToken } from 'axios';
import { useAppDispatch, useAppSelector } from '../store';
import {
  getOptionsSupplierEmployee,
  optionsSupplierEmployeeActions,
  selectOptionsSupplierEmployee,
  selectOptionsSupplierEmployeeState
} from '../store/entities/optionsSupplierEmployee';
import {
  getOptionsInvitationTypeVisit,
  optionsInvitationTypeVisitActions,
  selectOptionsInvitationTypeVisit,
  selectOptionsInvitationTypeVisitState
} from '../store/entities/optionsInvitationTypeVisit';
import { FetchState } from '../store/fetchState';
import { getFirstLastNameSupplierEmployee } from '../utils/format';
import { UserRole } from '@api/models/enums/UserRole';
import { UserState } from '@api/models/enums/UserState';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';

/**
 * Minimal shape shared by SupplierEmployeeDto and ReferenceDto for autocomplete usage.
 * Both types satisfy this interface, eliminating forced casts between them.
 */
export interface SupplierEmployeeOptionDto {
  id?: string;
  name?: string;
  lastName?: string;
}

interface Params {
  isVisit: boolean;
  supplierId?: string;
  role?: UserRole;
  state?: UserState[];
}

interface Result {
  options: SupplierEmployeeOptionDto[];
  loading: boolean;
  loadingError: boolean;
  loadOptions: (search: string | undefined, cancelToken: CancelToken) => void;
  handleGetValueIfNotFound: (option: SupplierEmployeeOptionDto) => SupplierEmployeeOptionDto;
}

export const useSupplierEmployeeOrVisitOptions = ({ isVisit, supplierId, role, state }: Params): Result => {
  const dispatch = useAppDispatch();
  const optionsSupplierEmployee = useAppSelector(selectOptionsSupplierEmployee);
  const optionsSupplierEmployeeState = useAppSelector(selectOptionsSupplierEmployeeState);
  const optionsInvitationTypeVisit = useAppSelector(selectOptionsInvitationTypeVisit);
  const optionsInvitationTypeVisitState = useAppSelector(selectOptionsInvitationTypeVisitState);

  const options: SupplierEmployeeOptionDto[] = isVisit ? optionsInvitationTypeVisit : optionsSupplierEmployee;
  const loadState = isVisit ? optionsInvitationTypeVisitState : optionsSupplierEmployeeState;

  const loadOptions = (search: string | undefined, cancelToken: CancelToken) => {
    if (isVisit) {
      dispatch(getOptionsInvitationTypeVisit(search, cancelToken));
    } else if (supplierId) {
      dispatch(getOptionsSupplierEmployee(supplierId, search, role, state, cancelToken));
    } else {
      dispatch(optionsSupplierEmployeeActions.default());
      dispatch(optionsInvitationTypeVisitActions.default());
    }
  };

  const handleGetValueIfNotFound = (option: SupplierEmployeeOptionDto): SupplierEmployeeOptionDto => {
    if (isVisit) return option;
    // Safe cast: when !isVisit, options are always SupplierEmployeeDto from the Redux slice
    return getFirstLastNameSupplierEmployee(option as SupplierEmployeeDto);
  };

  return {
    options,
    loading: loadState === FetchState.Loading,
    loadingError: loadState === FetchState.Error,
    loadOptions,
    handleGetValueIfNotFound,
  };
};
